// app/javascript/controllers/tom_select_controller.js
import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import Swal from 'sweetalert2'

export default class extends Controller {
  static targets = ["select"]
  static values = {
    search: Boolean,
    placeholder: String,
    maxItems: Number,
    create: Boolean,
    users: Array,
    skills: Array,
    sectors: Array,
    mode: String, // 'users', 'skills', 'sectors', 'sector-experience', 'simple'
    loadUrl: String,
    existingData: Object
  }

  connect() {
    this.initializeTomSelect()
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  initializeTomSelect() {
    const selectElement = this.selectTarget
    const mode = this.modeValue || 'users'

    const baseOptions = {
      plugins: ['remove_button'],
      placeholder: this.placeholderValue || 'Sélectionner...',
      maxItems: this.maxItemsValue || null,
      persist: false,
      createOnBlur: true,
      highlight: true
    }

    // Configuration spécifique selon le mode
    let options = { ...baseOptions }

    switch (mode) {
      case 'users':
        options = { ...options, ...this.getUsersConfig() }
        break
      case 'skills':
        options = { ...options, ...this.getSkillsConfig() }
        break
      case 'sectors':
        options = { ...options, ...this.getSectorsConfig() }
        break
      case 'sector-experience':
        options = { ...options, ...this.getSectorExperienceConfig() }
        break
      case 'simple':
        options = { ...options, ...this.getSimpleConfig() }
        break
      default:
        options = { ...options, ...this.getSimpleConfig() }
    }

    this.tomSelect = new TomSelect(selectElement, options)

    // Événements communs
    this.setupCommonEvents()

    // Charger les données existantes si nécessaire
    if (this.hasExistingDataValue && mode === 'sector-experience') {
      this.loadExistingSectorExperience()
    }
  }

  // ===============================
  // CONFIGURATIONS PAR MODE
  // ===============================

  getUsersConfig() {
    return {
      valueField: 'value',
      labelField: 'text',
      searchField: ['text', 'email'],
      options: this.buildUserOptions(),
      create: this.createValue ? this.createUser.bind(this) : false,
      createFilter: this.createValue ? this.validateEmail.bind(this) : null,
      render: {
        option: this.renderUserOption.bind(this),
        item: this.renderUserItem.bind(this),
        no_results: () => `<div class="py-3 px-3 text-center text-gray-500">
          <div class="mb-2">Aucun utilisateur trouvé</div>
          <div class="text-sm">Tapez un email pour créer un nouvel utilisateur</div>
        </div>`
      }
    }
  }

  getSkillsConfig() {
    return {
      valueField: 'id',
      labelField: 'name',
      searchField: ['name', 'description'],
      options: this.buildSkillOptions(),
      create: this.createValue ? this.createSkill.bind(this) : false,
      load: this.hasLoadUrlValue ? this.loadSkills.bind(this) : null,
      render: {
        option: this.renderSkillOption.bind(this),
        item: this.renderSkillItem.bind(this),
        no_results: () => `<div class="py-3 px-3 text-center text-gray-500">
          <div class="mb-2">Aucune compétence trouvée</div>
          <div class="text-sm">Tapez pour créer une nouvelle compétence</div>
        </div>`
      }
    }
  }

  getSectorsConfig() {
    return {
      valueField: 'value',
      labelField: 'text',
      searchField: ['text'],
      options: this.buildSectorOptions(),
      create: this.createValue ? this.createSector.bind(this) : false,
      render: {
        option: this.renderSectorOption.bind(this),
        item: this.renderSectorItem.bind(this),
        no_results: () => `<div class="py-3 px-3 text-center text-gray-500">
          <div class="mb-2">Aucun secteur trouvé</div>
          <div class="text-sm">Tapez pour créer un nouveau secteur</div>
        </div>`
      }
    }
  }

  getSectorExperienceConfig() {
    return {
      valueField: 'value',
      labelField: 'display',
      searchField: ['sector', 'display'],
      options: this.buildSectorExperienceOptions(),
      create: this.createValue ? this.createSectorExperience.bind(this) : false,
      render: {
        option: this.renderSectorExperienceOption.bind(this),
        item: this.renderSectorExperienceItem.bind(this),
        no_results: () => `<div class="py-3 px-3 text-center text-gray-500">
          <div class="mb-2">Aucun secteur trouvé</div>
          <div class="text-sm">Tapez un secteur pour l'ajouter</div>
        </div>`
      }
    }
  }

  getSimpleConfig() {
    // Configuration simple pour les selects standards (organisations, topics, expertises, etc.)
    return {
      plugins: this.maxItemsValue && this.maxItemsValue > 1 ? ['remove_button'] : [],
      placeholder: this.placeholderValue || 'Sélectionner...',
      maxItems: this.maxItemsValue || null,
      allowEmptyOption: true,
      render: {
        option: (data, escape) => {
          return `<div class="py-2 px-3 hover:bg-blue-50 dark:hover:bg-gray-700">
            <div class="font-medium text-gray-900 dark:text-white">${escape(data.text)}</div>
          </div>`
        },
        item: (data, escape) => {
          return `<div class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            ${escape(data.text)}
          </div>`
        },
        no_results: () => `<div class="py-3 px-3 text-center text-gray-500 dark:text-gray-400">
          <div class="mb-2">Aucun résultat trouvé</div>
        </div>`
      }
    }
  }

  // ===============================
  // CONSTRUCTION DES OPTIONS
  // ===============================

  buildUserOptions() {
    if (!this.hasUsersValue) return []
    return this.usersValue.map(user => ({
      value: user.id.toString(),
      text: user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email,
      email: user.email,
      isNew: false
    }))
  }

  buildSkillOptions() {
    if (!this.hasSkillsValue) return []
    return this.skillsValue.map(skill => ({
      id: skill.id,
      name: skill.name,
      description: skill.description || ''
    }))
  }

  buildSectorOptions() {
    if (!this.hasSectorsValue) return []
    return this.sectorsValue.map(sector => ({
      value: sector,
      text: sector
    }))
  }

  buildSectorExperienceOptions() {
    const options = []
    
    // Ajouter TOUS les secteurs suggérés SANS durée (format "secteur|")
    // pour qu'ils déclenchent systématiquement la demande de durée
    if (this.hasSectorsValue) {
      this.sectorsValue.forEach(sector => {
        options.push({
          value: `${sector}|`, // Format "secteur|" pour déclencher la demande de durée
          sector: sector,
          duration: '',
          display: sector
        })
      })
    }

    // Ajouter les expériences déjà saisies (avec durée)
    if (this.hasExistingDataValue) {
      Object.entries(this.existingDataValue).forEach(([sector, duration]) => {
        // Remplacer l'option sans durée par celle avec durée
        const indexToReplace = options.findIndex(opt => opt.sector.toLowerCase() === sector.toLowerCase())
        
        const optionWithDuration = {
          value: `${sector}|${duration}`,
          sector: sector,
          duration: duration,
          display: `${sector} (${duration})`
        }
        
        if (indexToReplace >= 0) {
          options[indexToReplace] = optionWithDuration
        } else {
          options.push(optionWithDuration)
        }
      })
    }

    return options
  }

  // ===============================
  // MÉTHODES DE CRÉATION
  // ===============================

  createUser(input, callback) {
    if (this.isValidEmail(input)) {
      callback({
        value: input,
        text: input,
        email: input,
        isNew: true
      })
    } else {
      callback()
    }
  }

  createSkill(input, callback) {
    const skillName = input.trim()
    if (skillName.length < 2) {
      callback()
      return
    }

    // Vérifier si la compétence existe déjà localement
    const existingSkill = this.buildSkillOptions().find(skill => 
      skill.name.toLowerCase() === skillName.toLowerCase()
    )
    
    if (existingSkill) {
      callback(existingSkill)
      return
    }

    // Créer immédiatement l'option avec un ID temporaire
    const tempId = `new_${Date.now()}`
    const newSkill = {
      id: tempId,
      name: skillName,
      description: '',
      isNew: true,
      tempName: skillName  // Stocker le nom pour le backend
    }

    // Stocker le mapping ID temporaire -> nom dans un champ caché
    this.storeNewSkillMapping(tempId, skillName)
    
    callback(newSkill)
  }

  createSector(input, callback) {
    const sectorName = input.trim().toLowerCase()
    if (sectorName.length >= 2) {
      callback({
        value: sectorName,
        text: sectorName
      })
    } else {
      callback()
    }
  }

  createSectorExperience(input, callback) {
    const sectorName = input.trim().toLowerCase()
    if (sectorName.length < 2) {
      callback()
      return
    }

    // TOUJOURS demander la durée, peu importe si le secteur existe ou non
    this.promptForDurationWithSweetAlert(sectorName, callback)
  }

  // ===============================
  // MÉTHODES DE RENDU
  // ===============================

  renderUserOption(data, escape) {
    if (data.isNew) {
      return `<div class="py-2 px-3 hover:bg-blue-50 border-l-4 border-blue-500">
        <div class="flex items-center">
          <svg class="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          <div>
            <div class="font-medium text-blue-700">Créer un nouvel utilisateur</div>
            <div class="text-sm text-blue-600">${escape(data.email)}</div>
          </div>
        </div>
      </div>`
    } else {
      return `<div class="py-2 px-3 hover:bg-gray-100">
        <div class="font-medium">${escape(data.text)}</div>
        <div class="text-sm text-gray-500">${escape(data.email)}</div>
      </div>`
    }
  }

  renderUserItem(data, escape) {
    if (data.isNew) {
      return `<div class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800">
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        ${escape(data.email)} (nouveau)
      </div>`
    } else {
      return `<div class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-green-100 text-green-800">
        ${escape(data.text)}
      </div>`
    }
  }

  renderSkillOption(data, escape) {
    return `<div class="py-2 px-3 hover:bg-gray-100">
      <div class="font-medium">${escape(data.name)}</div>
      ${data.description ? `<div class="text-sm text-gray-500">${escape(data.description)}</div>` : ''}
      ${data.isNew ? '<span class="text-xs text-blue-500">(nouveau)</span>' : ''}
    </div>`
  }

  renderSkillItem(data, escape) {
    return `<div class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-orange-100 text-orange-800">
      ${data.isNew ? '<i class="fa-solid fa-plus mr-1"></i>' : ''}
      ${escape(data.name)}
    </div>`
  }

  renderSectorOption(data, escape) {
    return `<div class="py-2 px-3 hover:bg-gray-100">
      <div class="font-medium capitalize">${escape(data.text)}</div>
    </div>`
  }

  renderSectorItem(data, escape) {
    return `<div class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-purple-100 text-purple-800">
      ${escape(data.text)}
    </div>`
  }

  renderSectorExperienceOption(data, escape) {
    if (data.duration) {
      // Secteur avec expérience déjà définie
      return `<div class="py-2 px-3 hover:bg-green-50 border-l-4 border-green-500">
        <div class="flex justify-between items-center">
          <span class="font-medium capitalize text-green-800">${escape(data.sector)}</span>
          <span class="text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">${escape(data.duration)}</span>
        </div>
        <div class="text-xs text-green-500 mt-1">
          <i class="fa-solid fa-check-circle mr-1"></i>
          Expérience définie
        </div>
      </div>`
    } else {
      // Secteur sans expérience - cliquer pour définir
      return `<div class="py-2 px-3 hover:bg-blue-50 border-l-4 border-blue-500">
        <div class="font-medium capitalize text-blue-800">${escape(data.sector)}</div>
        <div class="text-xs text-blue-500 mt-1">
          <i class="fa-solid fa-clock mr-1"></i>
          Cliquez pour définir votre expérience
        </div>
      </div>`
    }
  }

  renderSectorExperienceItem(data, escape) {
    if (data.duration) {
      return `<div class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-indigo-100 text-indigo-800">
        <span class="capitalize">${escape(data.sector)}</span>
        <span class="ml-1 text-xs opacity-75">(${escape(data.duration)})</span>
      </div>`
    }
    return `<div class="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-indigo-100 text-indigo-800">
      <span class="capitalize">${escape(data.sector)}</span>
    </div>`
  }

  // ===============================
  // GESTION DES ÉVÉNEMENTS
  // ===============================

  setupCommonEvents() {
    this.tomSelect.on('change', () => {
      const event = new CustomEvent('tom-select:change', {
        detail: { 
          value: this.tomSelect.getValue(),
          mode: this.modeValue 
        },
        bubbles: true
      })
      this.element.dispatchEvent(event)
    })

    this.tomSelect.on('item_add', (value, item) => {
      // Gestion spéciale pour les secteurs d'expérience
      if (this.modeValue === 'sector-experience') {
        this.handleSectorExperienceItemAdd(value, item)
      }

      // Si c'est une nouvelle compétence, on s'assure que le mapping est stocké
      if (this.modeValue === 'skills' && value.toString().startsWith('new_')) {
        const option = this.tomSelect.options[value]
        if (option && option.tempName) {
          this.storeNewSkillMapping(value, option.tempName)
        }
      }

      const event = new CustomEvent('tom-select:item-add', {
        detail: { 
          value: value,
          item: item,
          mode: this.modeValue 
        },
        bubbles: true
      })
      this.element.dispatchEvent(event)
    })

    this.tomSelect.on('item_remove', (value) => {
      // Si on supprime une nouvelle compétence, nettoyer le mapping
      if (this.modeValue === 'skills' && value.toString().startsWith('new_')) {
        this.removeNewSkillMapping(value)
      }
    })
  }

  handleSectorExperienceItemAdd(value, item) {
    // Pour TOUS les secteurs d'expérience, vérifier s'il manque la durée
    if (value.includes('|')) {
      const [sector, duration] = value.split('|')
      
      // Si pas de durée ou durée vide, demander la durée
      if (!duration || duration.trim() === '') {
        // Supprimer temporairement l'élément pour éviter la duplication
        this.tomSelect.removeItem(value, true)
        
        // Demander la durée avec SweetAlert2
        this.promptForDurationWithSweetAlert(sector, (result) => {
          if (result && result.value) {
            // Ajouter l'élément avec la durée
            this.tomSelect.addOption(result)
            this.tomSelect.addItem(result.value, true)
            
            // Mettre à jour le champ caché
            this.updateHiddenFieldFromTomSelect(this.tomSelect.getValue())
          }
          // Si annulé, l'élément reste supprimé
        })
      }
    }
  }

  // ===============================
  // GESTION SWEETALERT2
  // ===============================

  promptForDurationWithSweetAlert(sector, callback) {
    // Utiliser SweetAlert2 au lieu de prompt natif
    Swal.fire({
      title: `Expérience en ${sector.charAt(0).toUpperCase() + sector.slice(1)}`,
      input: 'text',
      inputLabel: 'Quelle est votre durée d\'expérience ?',
      inputPlaceholder: 'ex: 2 ans, 6 mois, 3 années...',
      inputValue: '',
      showCancelButton: true,
      confirmButtonText: 'Ajouter',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#6b7280',
      inputValidator: (value) => {
        if (!value || value.trim().length < 1) {
          return 'Veuillez indiquer une durée d\'expérience'
        }
        if (value.trim().length > 50) {
          return 'La durée ne peut pas dépasser 50 caractères'
        }
        return null
      },
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'px-4 py-2 rounded-md',
        cancelButton: 'px-4 py-2 rounded-md'
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const duration = result.value.trim()
        const value = `${sector}|${duration}`
        
        callback({
          value: value,
          sector: sector,
          duration: duration,
          display: `${sector} (${duration})`
        })
        
        // Optionnel : Toast de confirmation
        this.showSuccessToast(`Expérience en ${sector} ajoutée : ${duration}`)
      } else {
        // Annulé - ne pas ajouter l'élément
        callback()
      }
    }).catch(() => {
      // Fallback vers prompt natif en cas d'erreur
      this.promptForExperienceDurationFallback(sector, callback)
    })
  }

  showSuccessToast(message) {
    // Toast de confirmation optionnel
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: message,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        popup: 'rounded-lg text-sm'
      }
    })
  }

  // Fallback si SweetAlert2 n'est pas disponible
  promptForExperienceDurationFallback(sector, callback) {
    const duration = prompt(`Quelle est votre expérience en ${sector} ?`, '2 ans')
    if (duration && duration.trim()) {
      callback({
        value: `${sector}|${duration.trim()}`,
        sector: sector,
        duration: duration.trim(),
        display: `${sector} (${duration.trim()})`
      })
    } else {
      callback()
    }
  }

  // ===============================
  // GESTION DES COMPÉTENCES
  // ===============================

  storeNewSkillMapping(tempId, skillName) {
    // Créer ou mettre à jour un champ caché avec les mappings des nouvelles compétences
    let hiddenField = document.getElementById('new-skills-mapping')
    
    if (!hiddenField) {
      hiddenField = document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.id = 'new-skills-mapping'
      hiddenField.name = 'person[new_skills_mapping]'
      this.element.closest('form').appendChild(hiddenField)
    }

    let mappings = {}
    if (hiddenField.value) {
      try {
        mappings = JSON.parse(hiddenField.value)
      } catch(e) {
        mappings = {}
      }
    }

    mappings[tempId] = skillName
    hiddenField.value = JSON.stringify(mappings)
    
  }

  removeNewSkillMapping(tempId) {
    const hiddenField = document.getElementById('new-skills-mapping')
    if (!hiddenField || !hiddenField.value) return

    try {
      let mappings = JSON.parse(hiddenField.value)
      delete mappings[tempId]
      hiddenField.value = JSON.stringify(mappings)
    } catch(e) {
    }
  }

  // ===============================
  // GESTION DES SECTEURS D'EXPÉRIENCE
  // ===============================

  updateHiddenFieldFromTomSelect(values) {
    const experienceData = {}
    
    if (Array.isArray(values)) {
      values.forEach(value => {
        const [sector, duration] = value.split('|')
        if (sector && duration) {
          experienceData[sector] = duration
        }
      })
    }

    // Trouver le champ caché experience_sectorielle
    const hiddenField = document.querySelector('input[name="person[experience_sectorielle]"]')
    if (hiddenField) {
      hiddenField.value = JSON.stringify(experienceData)
    }

    this.dispatchExperienceUpdate(experienceData)
  }

  dispatchExperienceUpdate(data) {
    const event = new CustomEvent('sector-experience:updated', {
      detail: { data: data },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  loadExistingSectorExperience() {
    if (!this.hasExistingDataValue) return

    Object.entries(this.existingDataValue).forEach(([sector, duration]) => {
      const value = `${sector}|${duration}`
      this.tomSelect.addOption({
        value: value,
        sector: sector,
        duration: duration,
        display: `${sector} (${duration})`
      })
      this.tomSelect.addItem(value, true)
    })
  }

  // ===============================
  // MÉTHODES UTILITAIRES
  // ===============================

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email.trim())
  }

  validateEmail(input) {
    return this.isValidEmail(input) && !this.emailExists(input)
  }

  emailExists(email) {
    if (!this.hasUsersValue) return false
    return this.usersValue.some(user => user.email.toLowerCase() === email.toLowerCase().trim())
  }

  loadSkills(query, callback) {
    if (!query.length) return callback()
    
    fetch(`${this.loadUrlValue}?q=${encodeURIComponent(query)}`)
      .then(response => response.json())
      .then(json => callback(json))
      .catch(() => callback())
  }

  // Méthode pour vérifier si SweetAlert2 est disponible
  isSweetAlert2Available() {
    return typeof Swal !== 'undefined'
  }

  // Override de la méthode originale pour s'assurer qu'on utilise la bonne logique
  promptForExperienceDuration(sector, callback) {
    if (this.isSweetAlert2Available()) {
      this.promptForDurationWithSweetAlert(sector, callback)
    } else {
      this.promptForExperienceDurationFallback(sector, callback)
    }
  }
}